import { v4 as uuidv4 } from 'uuid';
import { BuildError } from '../types';

let pendingErrors: BuildError[] = [];

export function parseViteErrorHtml(html: string): any {
  try {
    // Extract the error object from Vite's HTML error page
    const errorStart = html.indexOf('const error = {');
    if (errorStart === -1) {
      return null;
    }
    
    // Find the complete JSON object by counting braces
    let jsonStart = errorStart + 'const error = '.length;
    let braceCount = 0;
    let inString = false;
    let escaped = false;
    let jsonEnd = jsonStart;
    
    for (let i = jsonStart; i < html.length; i++) {
      const char = html[i];
      
      if (escaped) {
        escaped = false;
        continue;
      }
      
      if (char === '\\') {
        escaped = true;
        continue;
      }
      
      if (char === '"') {
        inString = !inString;
        continue;
      }
      
      if (!inString) {
        if (char === '{') {
          braceCount++;
        } else if (char === '}') {
          braceCount--;
          if (braceCount === 0) {
            jsonEnd = i + 1;
            break;
          }
        }
      }
    }
    
    if (braceCount === 0) {
      const errorJson = html.substring(jsonStart, jsonEnd);
      try {
        const errorObj = JSON.parse(errorJson);
        return errorObj;
      } catch (parseError) {
        console.error('Failed to parse error JSON:', parseError);
        // Try to extract key parts manually
        const messageMatch = errorJson.match(/"message":"([^"\\]*(\\.[^"\\]*)*)"/);
        const idMatch = errorJson.match(/"id":"([^"\\]*(\\.[^"\\]*)*)"/);
        const locMatch = errorJson.match(/"loc":\{[^}]*"file":"([^"\\]*(\\.[^"\\]*)*)","line":(\d+),"column":(\d+)/);
        
        if (messageMatch) {
          return {
            message: messageMatch[1].replace(/\\"/g, '"'),
            file: idMatch ? idMatch[1] : (locMatch ? locMatch[1] : undefined),
            line: locMatch ? parseInt(locMatch[3]) : undefined,
            column: locMatch ? parseInt(locMatch[4]) : undefined
          };
        }
      }
    }
    
    // If no JSON found, check for other error patterns
    if (html.includes('Internal Server Error') || html.includes('Error:')) {
      // Try to extract error message from HTML
      const messageMatch = html.match(/<h2[^>]*>([^<]+)<\/h2>/);
      if (messageMatch) {
        return { message: messageMatch[1] };
      }
    }
    
    return null;
  } catch (e) {
    console.error('Failed to parse Vite error HTML:', e);
    return null;
  }
}

export function sendErrorToParent(error: any) {
  console.log("SENDING ERROR TO PARENT", error)
  const buildError: BuildError = {
    id: uuidv4(),
    message: error.message || error.text || error.error || 'Build failed',
    count: 1,
    file: error.loc?.file || error.location?.file || error.fileName || error.id,
    line: error.loc?.line || error.location?.line || error.lineNumber || (error.loc?.line),
    column: error.loc?.column || error.location?.column || error.columnNumber || (error.loc?.column),
    stack: error.stack,
    timestamp: Date.now(),
    solved: false,
    severity: 'error',
    isSolving: false
  };

  // Store error for injection into HTML
  pendingErrors.push(buildError);
  
  // Keep only last 10 errors
  if (pendingErrors.length > 10) {
    pendingErrors = pendingErrors.slice(-10);
  }

  // For development, log to console as fallback
  if (process.env.NODE_ENV === 'development') {
    console.error('Error reported to parent:', buildError);
  }
}

export function getErrorReporterScript(): string {
  const errorsToSend = [...pendingErrors];
  pendingErrors = []; // Clear the pending errors

  return `
    <script>
      // Send any build errors that occurred
      const buildErrors = ${JSON.stringify(errorsToSend)};
      buildErrors.forEach(function(buildError) {
        if (window.parent && window.parent !== window) {
          console.log('Sending build error to parent:', buildError);
          window.parent.postMessage({
            type: 'console',
            method: 'error',
            args: [buildError],
            timestamp: new Date().toISOString()
          }, '*');
        }
      });

      // Simple UUID generator for browser
      function generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }

      // Global error handler for runtime errors
      window.addEventListener('error', function(event) {
        if (window.parent && window.parent !== window) {
          const runtimeError = {
            id: generateUUID(),
            message: event.message,
            count: 1,
            file: event.filename,
            line: event.lineno,
            column: event.colno,
            stack: event.error ? event.error.stack : '',
            timestamp: Date.now(),
            solved: false,
            severity: 'error',
            isSolving: false
          };
          console.log('Sending runtime error to parent:', runtimeError);
          window.parent.postMessage({
            type: 'console',
            method: 'error',
            args: [runtimeError],
            timestamp: new Date().toISOString()
          }, '*');
        }
      });

      // Handle unhandled promise rejections
      window.addEventListener('unhandledrejection', function(event) {
        if (window.parent && window.parent !== window) {
          const rejectionError = {
            id: generateUUID(),
            message: event.reason ? event.reason.message || event.reason : 'Unhandled promise rejection',
            count: 1,
            stack: event.reason ? event.reason.stack : '',
            timestamp: Date.now(),
            solved: false,
            severity: 'error',
            isSolving: false
          };
          console.log('Sending rejection error to parent:', rejectionError);
          window.parent.postMessage({
            type: 'console',
            method: 'error',
            args: [rejectionError],
            timestamp: new Date().toISOString()
          }, '*');
        }
      });
    </script>
  `;
} 